import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import Input from '../components/atoms/Input';
import SessionManager from '../utils/sessionManager';

const SuperAdminPackageManagement = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const session = SessionManager.getCurrentSession();

  const [formData, setFormData] = useState({
    plan_name: '',
    plan_tier: 'basic',
    display_order: 0,
    ai_trainer_credits: 0,
    enhance_cv_credits: 0,
    job_apply_credits: 0,
    placement_training_credits: 0,
    price_inr: 0,
    discount_percentage: 0,
    description: '',
    features: [],
    badge: '',
    validity_days: 30,
    is_active: true
  });

  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    if (!session || session.role !== 'super_admin') {
      navigate('/');
      return;
    }
    loadPackages();
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/packages/admin/plans');
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingPackage
        ? `/api/packages/admin/plans/${editingPackage.id}?admin_id=${session.user_id}`
        : `/api/packages/admin/plans?admin_id=${session.user_id}`;
      
      const method = editingPackage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert(`✅ Package ${editingPackage ? 'updated' : 'created'} successfully!`);
        setShowForm(false);
        setEditingPackage(null);
        resetForm();
        loadPackages();
      } else {
        const error = await response.json();
        alert(`❌ ${error.detail || 'Failed to save package'}`);
      }
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Error saving package');
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      plan_name: pkg.plan_name,
      plan_tier: pkg.plan_tier,
      display_order: pkg.display_order,
      ai_trainer_credits: pkg.ai_trainer_credits,
      enhance_cv_credits: pkg.enhance_cv_credits,
      job_apply_credits: pkg.job_apply_credits,
      placement_training_credits: pkg.placement_training_credits,
      price_inr: pkg.price_inr,
      discount_percentage: pkg.discount_percentage,
      description: pkg.description || '',
      features: pkg.features || [],
      badge: pkg.badge || '',
      validity_days: pkg.validity_days,
      is_active: pkg.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const response = await fetch(`/api/packages/admin/plans/${packageId}?admin_id=${session.user_id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('✅ Package deleted successfully!');
        loadPackages();
      } else {
        alert('❌ Failed to delete package');
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      alert('Error deleting package');
    }
  };

  const resetForm = () => {
    setFormData({
      plan_name: '',
      plan_tier: 'basic',
      display_order: 0,
      ai_trainer_credits: 0,
      enhance_cv_credits: 0,
      job_apply_credits: 0,
      placement_training_credits: 0,
      price_inr: 0,
      discount_percentage: 0,
      description: '',
      features: [],
      badge: '',
      validity_days: 30,
      is_active: true
    });
    setFeatureInput('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading packages...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Package Management</h1>
            <p className="mt-2 text-gray-600">Manage student package plans and pricing</p>
          </div>
          <Button
            onClick={() => {
              setShowForm(true);
              setEditingPackage(null);
              resetForm();
            }}
            className="inline-flex items-center gap-2"
          >
            <Icon name="add" size={20} />
            Create Package
          </Button>
        </div>

        {/* Package Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingPackage ? 'Edit Package' : 'Create New Package'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingPackage(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="close" size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan Name
                  </label>
                  <Input
                    name="plan_name"
                    value={formData.plan_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan Tier
                  </label>
                  <select
                    name="plan_tier"
                    value={formData.plan_tier}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="basic">Basic</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                {/* Credits */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AI Trainer Credits
                  </label>
                  <Input
                    type="number"
                    name="ai_trainer_credits"
                    value={formData.ai_trainer_credits}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enhance CV Credits
                  </label>
                  <Input
                    type="number"
                    name="enhance_cv_credits"
                    value={formData.enhance_cv_credits}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Apply Credits
                  </label>
                  <Input
                    type="number"
                    name="job_apply_credits"
                    value={formData.job_apply_credits}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Placement Training Credits
                  </label>
                  <Input
                    type="number"
                    name="placement_training_credits"
                    value={formData.placement_training_credits}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>

                {/* Pricing */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (INR)
                  </label>
                  <Input
                    type="number"
                    name="price_inr"
                    value={formData.price_inr}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount %
                  </label>
                  <Input
                    type="number"
                    name="discount_percentage"
                    value={formData.discount_percentage}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                  />
                </div>

                {/* Other Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Validity (Days)
                  </label>
                  <Input
                    type="number"
                    name="validity_days"
                    value={formData.validity_days}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Badge (Optional)
                  </label>
                  <Input
                    name="badge"
                    value={formData.badge}
                    onChange={handleInputChange}
                    placeholder="e.g., Most Popular, Best Value"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Order
                  </label>
                  <Input
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Features
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    placeholder="Add a feature"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddFeature();
                      }
                    }}
                  />
                  <Button type="button" onClick={handleAddFeature} size="sm">
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span className="text-sm text-gray-700">{feature}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFeature(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Icon name="close" size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingPackage(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPackage ? 'Update Package' : 'Create Package'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Package List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-white rounded-xl shadow-md p-6 ${
                !pkg.is_active ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{pkg.plan_name}</h3>
                  <span className="text-xs text-gray-500 uppercase">{pkg.plan_tier}</span>
                  {pkg.badge && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {pkg.badge}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(pkg)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Icon name="edit" size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(pkg.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Icon name="delete" size={18} />
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-2xl font-bold text-gray-900">
                  ₹{pkg.price_inr.toLocaleString('en-IN')}
                </p>
                {pkg.discount_percentage > 0 && (
                  <p className="text-xs text-green-600">Save {pkg.discount_percentage}%</p>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">AI Trainer:</span>
                  <span className="font-semibold">{pkg.ai_trainer_credits}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">CV Enhance:</span>
                  <span className="font-semibold">{pkg.enhance_cv_credits}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Job Apply:</span>
                  <span className="font-semibold">{pkg.job_apply_credits}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Placement:</span>
                  <span className="font-semibold">{pkg.placement_training_credits}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Valid for {pkg.validity_days} days
                </p>
                <p className="text-xs text-gray-500">
                  Status: {pkg.is_active ? '✅ Active' : '❌ Inactive'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SuperAdminPackageManagement;
